import { useState } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { experienceUrl } from "@/lib/experience-url";
import { Sun, Snowflake, Leaf, TreeDeciduous, CalendarDays, Award } from "lucide-react";
import { IconPin, IconHourglass, IconPetal, IconVerified } from "@/components/icons";
import { FavoriteButton } from "./FavoriteButton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import type { ExperienceWithPricing } from "@/hooks/use-experiences";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import { useCurrency } from "@/contexts/CurrencyContext";
import guestlyLogoMark from "@/assets/guestly-logo-mark.svg";

const SEASON_ICONS: Record<string, React.ReactNode> = {
  summer: <Sun className="h-3 w-3" />,
  winter: <Snowflake className="h-3 w-3" />,
  spring: <Leaf className="h-3 w-3" />,
  autumn: <TreeDeciduous className="h-3 w-3" />,
  year_round: <CalendarDays className="h-3 w-3" />,
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatCardPrice(exp: ExperienceWithPricing, pricingOnEnquiry: string, formatPrice: (n: number) => string): string {
  if (exp.pricing_model === "PER_PERSON" && exp.grossPricePerPerson) {
    return formatPrice(exp.grossPricePerPerson);
  }
  if (exp.pricing_model === "PER_GROUP" && exp.grossPricePerGroup) {
    return formatPrice(exp.grossPricePerGroup);
  }
  if (exp.pricing_model === "BOTH" && exp.grossPricePerPerson) {
    return formatPrice(exp.grossPricePerPerson);
  }
  return pricingOnEnquiry;
}

interface ExperienceCardProps {
  experience: ExperienceWithPricing;
  compact?: boolean;
  reviewScore?: { avgScore: number; count: number } | null;
}

export function ExperienceCard({ experience, compact = false, reviewScore }: ExperienceCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useUiTranslations();
  const { formatPrice } = useCurrency();
  const seasonKey = experience.season ?? "year_round";
  const inSeason = experience.in_season ?? true;
  const seasonLabel = t(`season.${seasonKey}`);
  const imgs = experience.images && experience.images.length > 0 ? experience.images : ["/placeholder.svg"];
  const heroImage = imgs[0];
  const isFeatured = experience.featured;

  return (
    <LocalizedLink to={experienceUrl(experience.slug, (experience as any).city_slug)}>
      <Card className="group overflow-hidden border-border/50 bg-card transition-shadow hover:shadow-lg">
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative"
        >
          <AspectRatio ratio={compact ? 3 / 2 : 4 / 3}>
            <img
              src={heroImage}
              alt={experience.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </AspectRatio>

          {/* Overlay badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5">
            {isFeatured && (
              <Badge className="bg-amber-500/90 text-white border-0 gap-1 text-[11px] font-semibold backdrop-blur-sm">
                <Award className="h-3 w-3" />
                {t("card.topRated")}
              </Badge>
            )}
          </div>

          {/* Favorite button */}
          <div className="absolute top-2 right-2">
            <FavoriteButton experienceId={experience.id} />
          </div>

          {/* Certified badge bottom-right */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-1 backdrop-blur-sm">
            <img
              src={guestlyLogoMark}
              alt=""
              className="h-3.5 w-3.5"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <span className="text-[10px] font-medium text-white">{t("card.certified")}</span>
          </div>
        </div>

        <CardContent className={`space-y-2 ${compact ? "p-3" : "p-4"}`}>
          <div className="flex items-start justify-between gap-2">
            <h3 className={`line-clamp-1 font-semibold text-foreground ${compact ? "text-sm" : "text-base"}`}>
              {experience.title}
            </h3>
            {reviewScore && reviewScore.count > 0 && (
              <div className="flex items-center gap-1 shrink-0">
                <IconPetal className="h-3.5 w-3.5 fill-amber-brand text-amber-brand" filled />
                <span className="text-xs font-semibold text-foreground">{reviewScore.avgScore}</span>
                <span className="text-[10px] text-muted-foreground">({reviewScore.count})</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {experience.location && (
              <span className="flex items-center gap-1">
                <IconPin className="h-3 w-3" />
                {experience.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <IconHourglass className="h-3 w-3" />
              {formatDuration(experience.duration_minutes)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <p className={`font-semibold text-foreground ${compact ? "text-sm" : "text-sm"}`}>
              {formatCardPrice(experience, t("card.pricingOnEnquiry"), formatPrice)}
              <span className="font-normal text-muted-foreground text-xs ml-1">
                {experience.pricing_model === "PER_GROUP" ? `/ ${t("card.group")}` : `/ ${t("card.person")}`}
              </span>
            </p>
            {seasonKey !== "year_round" && !compact && (
              <Badge variant="outline" className="gap-1 text-[10px] font-normal">
                {SEASON_ICONS[seasonKey]}
                {inSeason ? seasonLabel : t("season.backIn").replace("{season}", seasonLabel.toLowerCase())}
              </Badge>
            )}
          </div>

          {!compact && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-brand">
              <IconVerified className="h-3 w-3" />
              {t("card.freeCancellation")}
            </span>
          )}
        </CardContent>
      </Card>
    </LocalizedLink>
  );
}
